import { memo, useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil, values } from 'lodash';
// Import Types
import type { PushNotificationProvider } from 'types';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useToaster from 'hooks/UseToaster';
// Import Services
import { Helpers } from 'services';
import { ProviderHttpService } from 'services/http';
// Import UI Components
import { Alert, Button } from '@geneui/components';
//Import Components
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
import KeyValueItem from 'components/common/KeyValueItem';
import UploadFile from 'components/common/UploadFile';
//Import Constants
import { l, AlertTypes, ModalsClassNames, CommonIcons, PushNotificationProviderKeys } from 'constants/common';

const FireBaseSettingsKeys = [
    'type',
    'project_id',
    'private_key_id',
    'private_key',
    'client_email',
    'client_id',
    'auth_uri',
    'token_uri',
    'auth_provider_x509_cert_url',
    'client_x509_cert_url',
    'universe_domain',
];

const { readTextFileAsync } = Helpers;
const { success, error } = AlertTypes;

interface Props {
    isDefault?: boolean;
    isLoading?: boolean;
    initialData?: PushNotificationProvider['Firebase'];
    onRefetchData?: () => void;
    onSetDefault?: (providerKey: number) => void;
}

const FireBaseSettingsContent: React.FC<Props> = (props) => {
    const { isDefault, isLoading, initialData, onRefetchData, onSetDefault } = props;
    const { t } = useTranslation();

    const { doPostRequest } = useRequest();
    const { showToaster } = useToaster();

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [selectedFileData, setSelectedFileData] = useState<Nullable<string>>(null);

    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);

    const [isClearSettingsLoading, setIsClearSettingsLoading] = useState(false);
    const [isUploadSettingsLoading, setIsUploadSettingsLoading] = useState(false);

    const isInitialDataEmpty = values(initialData).every((value) => isNil(value));

    const { setFirebaseKeyRequest, removeFirebaseKeyRequest } = useCancelableRequests({
        setFirebaseKeyRequest: ProviderHttpService.setFirebaseKey,
        removeFirebaseKeyRequest: ProviderHttpService.removeFirebaseKey,
    });

    const previewableSettingsFromSelectedFile = useMemo(() => {
        const parsedData = isNil(selectedFileData) ? {} : (JSON.parse(selectedFileData) ?? {});

        return {
            projectId: parsedData.project_id,
            privateKeyId: parsedData.private_key_id,
            clientId: parsedData.client_id,
            clientEmail: parsedData.client_email,
        };
    }, [selectedFileData]);

    const cancelUploadModal = useCallback(() => setIsUploadModalOpen(false), []);
    const cancelClearModal = useCallback(() => setIsClearModalOpen(false), []);
    const onClear = useCallback(() => setIsClearModalOpen(true), []);

    const checkValidation = (data) => {
        const keys = Object.keys(data);
        const values = Object.values(data);

        if (data.type !== 'service_account') return false;
        if (FireBaseSettingsKeys.some((key) => !keys.includes(key))) return false;
        if (values.some((value) => isEmpty(value))) return false;

        return true;
    };

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = e.currentTarget;

        if (!isNil(files?.[0])) {
            readTextFileAsync(files![0])
                .then((fileData) => {
                    if (checkValidation(JSON.parse(fileData))) {
                        setSelectedFileData(fileData);
                        setIsUploadModalOpen(true);
                    } else {
                        showToaster(error, t(l.FirebaseSettingsFileIsInvalid));
                    }
                })
                .catch(() => showToaster(error, t(l.FirebaseSettingsFileIsInvalid)));
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = null as unknown as string;
        }
    };

    const onSetDefaultHandler = () => {
        onSetDefault?.(PushNotificationProviderKeys.FireBase);
    };

    const updateSettings = () => {
        setIsUploadSettingsLoading(true);

        doPostRequest(setFirebaseKeyRequest.request, {
            requestBody: JSON.stringify(selectedFileData),
            successCallback: () => {
                onRefetchData?.();
                setIsUploadModalOpen(false);
                showToaster(success, t(l.FirebaseSettingsUploadedSuccessfully));
            },
        }).finally(() => {
            setIsUploadSettingsLoading(false);
        });
    };

    const clearSettings = () => {
        setIsClearSettingsLoading(true);

        doPostRequest(removeFirebaseKeyRequest.request, {
            successCallback: () => {
                onRefetchData?.();
                setIsClearModalOpen(false);
                showToaster(success, t(l.FirebaseSettingsClearedSuccessfully));
            },
        }).finally(() => {
            setIsClearSettingsLoading(false);
        });
    };

    return (
        <>
            <Alert closable={false} title={t(l.FirebaseDescription)} iconProps={{ type: 'bc-icon-info' }} />
            <div className="fields-btn-group">
                <KeyValueItem
                    label={l.ProjectId}
                    value={initialData?.ProjectId}
                    vertical={true}
                    isLoading={isLoading}
                />
                <KeyValueItem
                    label={l.PrivateKeyId}
                    value={initialData?.PrivateKeyId}
                    vertical={true}
                    isLoading={isLoading}
                />
                <KeyValueItem label={l.ClientId} value={initialData?.ClientId} vertical={true} isLoading={isLoading} />
                <div className="push-notification-settings-content-action-buttons-container">
                    <Button
                        appearance="outline"
                        onClick={onSetDefaultHandler}
                        disabled={isDefault || isInitialDataEmpty}
                    >
                        {t(l.SetDefault)}
                    </Button>
                    <Button
                        appearance="outline"
                        onClick={onClear}
                        disabled={isClearSettingsLoading || isEmpty(initialData) || isLoading}
                    >
                        {t(l.Clear)}
                    </Button>
                    <UploadFile
                        ref={fileInputRef}
                        onChange={onFileInputChange}
                        disabled={isUploadSettingsLoading || isLoading}
                        buttonTextKey={l.Upload}
                        showOnlyButton={true}
                        acceptedExtensions={'.json'}
                    />
                </div>
            </div>
            {isClearModalOpen && (
                <ConfirmationModal
                    onOk={clearSettings}
                    onCancel={cancelClearModal}
                    isVisibleModal={isClearModalOpen}
                    titleText={t(l.ClearSettings)}
                    questionLabel={t(l.ClearFirebaseSettingsDescription)}
                    iconType={CommonIcons.Info}
                    actionLabel={t(l.Continue)}
                    className={ModalsClassNames.UsedIn}
                    isLoading={isClearSettingsLoading}
                />
            )}
            {isUploadModalOpen && (
                <ConfirmationModal
                    onOk={updateSettings}
                    onCancel={cancelUploadModal}
                    isVisibleModal={isUploadModalOpen}
                    titleText={t(l.UploadSettings)}
                    questionLabel={t(l.UploadFirebaseSettingsDescription, {
                        projectId: `"${previewableSettingsFromSelectedFile.projectId}"`,
                        privateKeyId: `"${previewableSettingsFromSelectedFile.privateKeyId}"`,
                        clientId: `"${previewableSettingsFromSelectedFile.clientId}"`,
                        clientEmail: `"${previewableSettingsFromSelectedFile.clientEmail}"`,
                    })}
                    iconType={CommonIcons.Info}
                    actionLabel={t(l.Continue)}
                    className={ModalsClassNames.Warning}
                    isLoading={isUploadSettingsLoading}
                />
            )}
        </>
    );
};

export default memo(FireBaseSettingsContent);
